import { VoiceProse } from "@/components/ui/voice-prose";
import { type ReactNode, useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Kicker } from "@/components/ui/kicker";
import { VerdictChip } from "@/components/walkthrough-badges";
import {
  composeWalkthroughTone,
  toneBorderClass,
  verdictLabel,
  type VerdictRating,
} from "@/components/experience/vocabulary";
import { firstSentence } from "@/lib/plain-text";
import { cn } from "@/lib/utils";

export interface HumanTestVerdict {
  humanTestId?: string;
  question: string;
  rating: string;
  reasoning?: string | null;
}

type RenderReasoning = (text: string) => ReactNode;

/**
 * The judge's answers to a scenario's human tests — the summary of a run.
 * Each question reads as a sans headline with its verdict chip and the
 * gist of the judge's reasoning (its first sentence, serif — judge voice).
 * The rest of the reasoning is one "Show reasoning" away, in a tonal
 * block whose edge carries the verdict tone.
 */
export function HumanTestVerdicts({
  verdicts,
  note,
  renderReasoning = (text) => text,
  className,
}: {
  verdicts: HumanTestVerdict[];
  /** Muted context line beside the section label */
  note?: ReactNode;
  /** Render reasoning text — e.g. to link cited finding ids and steps */
  renderReasoning?: RenderReasoning;
  className?: string;
}) {
  const headingId = useId();
  return (
    <section className={cn("space-y-4", className)} aria-labelledby={headingId}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Kicker as="h2" className="text-foreground">
          <span id={headingId}>The human test</span>
        </Kicker>
        {note && <span className="text-sm text-muted-foreground">{note}</span>}
      </div>
      <ol className="space-y-6">
        {verdicts.map((v, i) => (
          <VerdictItem
            key={v.humanTestId ?? `verdict-${i}`}
            verdict={v}
            renderReasoning={renderReasoning}
          />
        ))}
      </ol>
    </section>
  );
}

function VerdictItem({
  verdict: v,
  renderReasoning,
}: {
  verdict: HumanTestVerdict;
  renderReasoning: RenderReasoning;
}) {
  const [open, setOpen] = useState(false);
  const restId = useId();
  const rating = v.rating as VerdictRating;
  const accent = toneBorderClass(composeWalkthroughTone("completed", rating));
  const reasoning = v.reasoning?.trim() ?? "";
  const summary = firstSentence(reasoning);
  const rest = reasoning.slice(summary.length).trim();

  return (
    <li
      className="space-y-3"
      aria-label={`${v.question}: ${verdictLabel(rating)}`}
    >
      <div className="flex items-start gap-3">
        <VerdictChip rating={rating} className="mt-0.5" />
        <div className="min-w-0 flex-1 space-y-2">
          <h3 className="text-md font-semibold">{v.question}</h3>
          {summary && (
            <VoiceProse className="max-w-[68ch] text-foreground">
              {renderReasoning(summary)}
            </VoiceProse>
          )}
          {rest && (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={restId}
              onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center gap-1 rounded-sm text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  "h-4 w-4 transition-transform",
                  open && "rotate-180",
                )}
              />
              {open ? "Hide reasoning" : "Show reasoning"}
            </button>
          )}
          {rest && (
            <div
              id={restId}
              hidden={!open}
              className={cn(
                "max-w-[68ch] border-l-[3px] bg-surface-tonal px-5 py-4",
                accent,
              )}
            >
              <VoiceProse className="text-foreground">
                {renderReasoning(rest)}
              </VoiceProse>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
